import styled from 'styled-components';
import { px2rem } from '../../utilities/UnitHelper';
import { ripple } from '../../mixins';

export const PrimaryButton = styled.button`
  ${(props) => props.theme.typography.button};
  ${ripple()};
  background-color: ${(props) => props.theme.colors.yellow.primary};
  border: 0;
  box-sizing: border-box;
  color: ${(props) => props.theme.colors.grayscale.black};
  cursor: pointer;
  display: inline-block;
  height: ${px2rem(56)};
  line-height: 1;
  overflow: hidden;
  padding: 0 1.5rem;
  position: relative;
  text-align: center;
  text-decoration: none;
  text-transform: capitalize;
  transform: translate3d(0, 0, 0);
  vertical-align: middle;
  min-width: ${px2rem(178)};

  &::before {
    content: '';
    display: inline-block;
    height: 100%;
    vertical-align: middle;
  }

  & svg {
    left: 50%;
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
  }

  & span {
    display: inline-block;
    text-decoration: inherit;
  }

  &:disabled {
    background-color: ${(props) => props.theme.colors.grayscale.gray2};
    color: ${(props) => props.theme.colors.grayscale.gray0};
    cursor: default;
  }

  &:hover:enabled {
    background-color: ${(props) => props.theme.colors.yellow.primaryHover};
  }

  &:focus:enabled {
    background-color: ${(props) => props.theme.colors.yellow.primary};
    box-shadow: 0 0 0 4px ${(props) => props.theme.colors.yellow.primary};
    outline: 2px solid ${(props) => props.theme.colors.grayscale.white};
  }

  &:active:enabled {
    background-color: ${(props) => props.theme.colors.yellow.primaryActive};
    text-transform: capitalize;
    outline: none;
    box-shadow: none;
  }
`;

export const ShortPrimaryButton = styled(PrimaryButton)`
  ${(props) => props.theme.typography.buttonSmall};
  height: 2.5rem;
  min-width: auto;
`;

export const HeavyButton = styled(PrimaryButton)`
  ${(props) => props.theme.typography.buttonHeavy};

  &:focus:enabled,
  &:active:enabled {
    text-transform: uppercase;
  }
`;

export const SecondaryButton = styled(PrimaryButton)`
  ${(props) => props.theme.borders.borderBlack1};
  background-color: ${(props) => props.theme.colors.grayscale.white};

  &:disabled {
    ${(props) => props.theme.borders.borderGray1_1};
    background-color: ${(props) => props.theme.colors.grayscale.white};
    color: ${(props) => props.theme.colors.grayscale.gray1};
  }

  &:hover:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.gray0};
    color: ${(props) => props.theme.colors.grayscale.white};
  }

  &:focus:enabled {
    ${(props) => props.theme.borders.borderBlack1};
    background-color: ${(props) => props.theme.colors.grayscale.gray0};
    box-shadow: none;
    color: ${(props) => props.theme.colors.grayscale.white};
  }

  &:active:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.black};
    color: ${(props) => props.theme.colors.grayscale.white};
    text-transform: capitalize;
  }
`;

export const ShortSecondaryButton = styled(SecondaryButton)`
  ${(props) => props.theme.typography.buttonSmall}
  height: 2.5rem;
  min-width: auto;
`;

export const FlatButton = styled(SecondaryButton)`
  background-color: transparent;
  border: 0;
  text-decoration: underline;

  &:disabled {
    background-color: transparent;
    border: 0;
  }

  &:focus:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.gray0};
    border: 0;
    box-shadow: 0 0 0 4px ${(props) => props.theme.colors.grayscale.gray0};
    outline: 2px solid ${(props) => props.theme.colors.grayscale.white};
  }

  &:active:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.black};
  }
`;

export const PrimaryIcon = styled(PrimaryButton)`
  line-height: 0;
  min-width: 0;
  width: ${px2rem(56)};

  &:disabled {
    & svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.gray1};
      }
    }
  }
`;

export const SecondaryIcon = styled(SecondaryButton)`
  ${(props) => props.theme.borders.borderBlue2};
  line-height: 0;
  min-width: 0;
  width: ${px2rem(56)};

  & svg {
    & path {
      fill: ${(props) => props.theme.colors.blue.primary};
    }
  }

  &:disabled {
    ${(props) => props.theme.borders.borderGray2_2};
    & svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.gray1};
      }
    }
  }

  &:hover:enabled {
    background-color: ${(props) => props.theme.colors.blue.primaryHover};
    border: 0;

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }

  &:focus:enabled {
    ${(props) => props.theme.borders.borderBlue2};
    background-color: ${(props) => props.theme.colors.blue.primaryHover};

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }

  &:active:enabled {
    background-color: ${(props) => props.theme.colors.blue.primaryActive};

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }
`;

export const FlatIcon = styled(FlatButton)`
  line-height: 0;
  min-width: 0;
  width: ${px2rem(56)};

  & svg {
    & path {
      fill: ${(props) => props.theme.colors.grayscale.black};
    }
  }

  &:disabled {
    & svg {
      & path {
        fill: ${(props) => props.theme.colors.grayscale.gray1};
      }
    }
  }

  &:hover:enabled {
    ${(props) => props.theme.borders.borderGray0_2};
    background-color: ${(props) => props.theme.colors.grayscale.gray0};

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }

  &:focus:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.gray0};
    border: 0;

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }

  &:active:enabled {
    background-color: ${(props) => props.theme.colors.grayscale.black};

    & svg path {
      fill: ${(props) => props.theme.colors.grayscale.white};
    }
  }
`;
